import React from 'react';
import { DocsLayout } from '../components/DocsLayout';

export const PrivacyPolicyPage: React.FC = () => {
  return (
    <DocsLayout
      title='Privacy Policy'
      description='How we collect, use, and protect your information'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Privacy Policy' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-8'>Last Updated: January 28, 2026</p>

        <section className='mb-20' id='introduction'>
          <h2 className='text-3xl font-bold mb-6'>1. Introduction</h2>
          <p className='text-sm leading-6'>
            Kebi AI LLC (referred to as <strong>Kebi</strong>, <strong>we</strong>,{' '}
            <strong>us</strong>, or <strong>our</strong>) operates the Vehix platform. This Privacy
            Policy explains how we collect, use, disclose, and safeguard your information when you
            use our service.
          </p>
        </section>

        <section className='mb-20' id='information-collection'>
          <h2 className='text-3xl font-bold mb-6'>2. Information We Collect</h2>

          <h3 className='text-lg font-semibold mb-4'>2.1 Information You Provide</h3>
          <ul className='list-disc pl-6 mb-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Account Information:</strong> Name, email address, and profile photo (via
              Google authentication)
            </li>
            <li className='text-sm leading-6'>
              <strong>Business Information:</strong> Organization name, dealer information,
              technician details
            </li>
            <li className='text-sm leading-6'>
              <strong>Vehicle Information:</strong> VINs, recall campaigns, service records
            </li>
            <li className='text-sm leading-6'>
              <strong>Communication Data:</strong> Support requests, feedback, and correspondence
            </li>
          </ul>

          <h3 className='text-lg font-semibold mb-4'>2.2 Automatically Collected Information</h3>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Usage Data:</strong> Pages viewed, features used, time spent
            </li>
            <li className='text-sm leading-6'>
              <strong>Technical Data:</strong> IP address, browser type, device information
            </li>
            <li className='text-sm leading-6'>
              <strong>Performance Data:</strong> Error logs, system performance metrics
            </li>
          </ul>
        </section>

        <section className='mb-20' id='how-we-use-your-information'>
          <h2 className='text-3xl font-bold mb-6'>3. How We Use Your Information</h2>
          <p className='text-sm leading-6 mb-4'>We use the collected information to:</p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Provide and maintain the Vehix platform</li>
            <li className='text-sm leading-6'>Process recall assignments and route planning</li>
            <li className='text-sm leading-6'>
              Communicate with you about your account and service
            </li>
            <li className='text-sm leading-6'>Improve our platform and develop new features</li>
            <li className='text-sm leading-6'>Ensure security and prevent fraud</li>
            <li className='text-sm leading-6'>Comply with legal obligations</li>
          </ul>
        </section>

        <section className='mb-20' id='how-vehix-helps-you'>
          <h2 className='text-3xl font-bold mb-6'>4. How Vehix Uses Your Data to Help You</h2>
          <p className='text-sm leading-6 mb-4'>
            Vehix uses your information to make helpful suggestions that save you time:
          </p>

          <h3 className='text-lg font-semibold mb-4'>4.1 Route Suggestions</h3>
          <p className='text-sm leading-6 mb-4'>
            We use dealer locations and recall information to suggest efficient routes for your
            technicians. This helps reduce travel time and complete more recalls.
          </p>

          <h3 className='text-lg font-semibold mb-4'>4.2 Technician Matching</h3>
          <p className='text-sm leading-6 mb-4'>
            We use technician skills, locations, and availability to suggest which technician might
            be best suited for each recall. You always make the final decision.
          </p>

          <h3 className='text-lg font-semibold mb-4'>4.3 You Stay in Control</h3>
          <p className='text-sm leading-6'>
            These suggestions are meant to help you, not replace your judgment. You can always
            change, override, or ignore any suggestion. We never make decisions without your
            approval.
          </p>
        </section>

        <section className='mb-20' id='your-data-stays-yours'>
          <h2 className='text-3xl font-bold mb-6'>5. Your Data Stays Yours</h2>

          <h3 className='text-lg font-semibold mb-4'>5.1 We Do Not Sell Your Data</h3>
          <p className='text-sm leading-6 mb-4'>
            We will never sell your personal information or business data to anyone. Your data is
            used only to provide the Vehix service to you.
          </p>

          <h3 className='text-lg font-semibold mb-4'>5.2 We Do Not Train AI on Your Data</h3>
          <p className='text-sm leading-6 mb-4'>
            Your data is not used to train artificial intelligence or machine learning systems. The
            suggestions Vehix makes are based on straightforward calculations, not on learning from
            your data.
          </p>

          <h3 className='text-lg font-semibold mb-4'>5.3 Your Data Is Not Shared for Marketing</h3>
          <p className='text-sm leading-6'>
            We do not share your data with advertisers or marketing companies. You will not receive
            ads based on your Vehix usage.
          </p>
        </section>

        <section className='mb-20' id='information-sharing'>
          <h2 className='text-3xl font-bold mb-6'>6. Information Sharing</h2>

          <h3 className='text-lg font-semibold mb-4'>4.1 We Share Information With:</h3>
          <ul className='list-disc pl-6 mb-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Your Organization:</strong> Authorized users within your dealership or dealer
              group
            </li>
            <li className='text-sm leading-6'>
              <strong>Service Providers:</strong> Third-party vendors who assist in operating our
              platform (e.g., hosting, analytics)
            </li>
            <li className='text-sm leading-6'>
              <strong>Legal Requirements:</strong> When required by law or to protect rights and
              safety
            </li>
          </ul>

          <h3 className='text-lg font-semibold mb-4'>4.2 We Do NOT:</h3>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Sell your personal information to third parties</li>
            <li className='text-sm leading-6'>Share your data with competitors</li>
            <li className='text-sm leading-6'>Use your data for unrelated marketing purposes</li>
          </ul>
        </section>

        <section className='mb-20' id='data-security'>
          <h2 className='text-3xl font-bold mb-6'>7. Data Security</h2>
          <p className='text-sm leading-6 mb-4'>
            We implement industry-standard security measures including:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Encryption in transit (TLS/SSL) and at rest</li>
            <li className='text-sm leading-6'>Regular security audits and penetration testing</li>
            <li className='text-sm leading-6'>Access controls and authentication</li>
            <li className='text-sm leading-6'>Employee training on data protection</li>
            <li className='text-sm leading-6'>Incident response procedures</li>
          </ul>
        </section>

        <section className='mb-20' id='data-retention'>
          <h2 className='text-3xl font-bold mb-6'>8. Data Retention</h2>
          <p className='text-sm leading-6 mb-4'>
            We retain your information for as long as your account is active or as needed to provide
            services. When you close your account, we retain certain information for:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              Legal compliance (typically 7 years for business records)
            </li>
            <li className='text-sm leading-6'>Dispute resolution</li>
            <li className='text-sm leading-6'>Fraud prevention</li>
          </ul>
        </section>

        <section className='mb-20' id='your-rights'>
          <h2 className='text-3xl font-bold mb-6'>9. Your Rights</h2>
          <p className='text-sm leading-6 mb-4'>
            Depending on your location, you may have the right to:
          </p>
          <ul className='list-disc pl-6 mb-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Access:</strong> Request a copy of your personal information
            </li>
            <li className='text-sm leading-6'>
              <strong>Correction:</strong> Update inaccurate or incomplete information
            </li>
            <li className='text-sm leading-6'>
              <strong>Deletion:</strong> Request deletion of your personal information
            </li>
            <li className='text-sm leading-6'>
              <strong>Portability:</strong> Receive your data in a machine-readable format
            </li>
            <li className='text-sm leading-6'>
              <strong>Objection:</strong> Object to certain processing of your information
            </li>
          </ul>

          <p className='text-sm leading-6'>
            To exercise these rights, contact us at <strong>privacy@kebi.ai</strong>
          </p>
        </section>

        <section className='mb-20' id='cookies-and-tracking'>
          <h2 className='text-3xl font-bold mb-6'>10. Cookies and Tracking</h2>
          <p className='text-sm leading-6 mb-4'>We use cookies and similar technologies to:</p>
          <ul className='list-disc pl-6 mb-6 space-y-2'>
            <li className='text-sm leading-6'>Maintain your session and authentication</li>
            <li className='text-sm leading-6'>Remember your preferences</li>
            <li className='text-sm leading-6'>Analyze platform usage and performance</li>
            <li className='text-sm leading-6'>Improve user experience</li>
          </ul>
          <p className='text-sm leading-6'>
            You can control cookies through your browser settings.
          </p>
        </section>

        <section className='mb-20' id='third-party-services'>
          <h2 className='text-3xl font-bold mb-6'>11. Third-Party Services</h2>
          <p className='text-sm leading-6 mb-4'>
            We work with trusted companies to help run Vehix. These include:
          </p>
          <ul className='list-disc pl-6 mb-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Google:</strong> Helps you sign in securely
            </li>
            <li className='text-sm leading-6'>
              <strong>Supabase:</strong> Stores your data securely in the cloud
            </li>
            <li className='text-sm leading-6'>
              <strong>OpenStreetMap:</strong> Provides map data and location services
            </li>
            <li className='text-sm leading-6'>
              <strong>Leaflet:</strong> Displays maps in the application
            </li>
          </ul>
          <p className='text-sm leading-6'>
            These companies only see the data they need to provide their services. They have their
            own privacy policies and are required to protect your information.
          </p>
        </section>

        <section className='mb-20' id='childrens-privacy'>
          <h2 className='text-3xl font-bold mb-6'>12. Privacy for Minors</h2>
          <p className='text-sm leading-6'>
            Vehix is not intended for individuals under 18 years of age. We do not knowingly collect
            personal information from children.
          </p>
        </section>

        <section className='mb-20' id='international-data-transfers'>
          <h2 className='text-3xl font-bold mb-6'>13. International Data Transfers</h2>
          <p className='text-sm leading-6'>
            Your information may be transferred to and processed in the United States. By using
            Vehix, you consent to such transfers. We ensure appropriate safeguards are in place for
            international data transfers.
          </p>
        </section>

        <section className='mb-20' id='changes-to-this-policy'>
          <h2 className='text-3xl font-bold mb-6'>14. Changes to This Policy</h2>
          <p className='text-sm leading-6 mb-4'>
            We may update this Privacy Policy from time to time. We will notify you of material
            changes by:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Posting the new policy on our platform</li>
            <li className='text-sm leading-6'>Updating the date at the top of this page</li>
            <li className='text-sm leading-6'>
              Sending an email notification (for significant changes)
            </li>
          </ul>
        </section>

        <section className='mb-20' id='contact-us'>
          <h2 className='text-3xl font-bold mb-6'>15. Contact Us</h2>
          <p className='text-sm leading-6 mb-6'>
            For questions about this Privacy Policy or our privacy practices, contact us at:
          </p>

          <div className='my-6 p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm space-y-1'>
              <strong>Kebi AI LLC</strong>
              <br />
              Email: <span className='text-primary'>privacy@kebi.ai</span>
              <br />
              Support: <span className='text-primary'>support@kebi.ai</span>
            </p>
          </div>
        </section>

        <section className='mb-20' id='california-privacy-rights'>
          <h2 className='text-3xl font-bold mb-6'>16. California Privacy Rights</h2>
          <p className='text-sm leading-6'>
            If you are a California resident, you have additional rights under the California
            Consumer Privacy Act (CCPA), including the right to know what personal information we
            collect and how we use it, and the right to request deletion of your personal
            information.
          </p>
        </section>

        <section className='mb-20' id='european-privacy-rights'>
          <h2 className='text-3xl font-bold mb-6'>17. European Privacy Rights</h2>
          <p className='text-sm leading-6'>
            If you are in the European Economic Area (EEA), you have rights under the General Data
            Protection Regulation (GDPR), including rights to access, rectification, erasure, and
            data portability.
          </p>
        </section>
      </div>
    </DocsLayout>
  );
};
